import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CheckCircleFilled, ExclamationCircleOutlined, WarningFilled } from "@ant-design/icons";
import { Button, Col, Popover, Row, Table, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 } from "uuid";
import { EMachineTranslationStatus } from "@devowl-wp/api";
import { ESupportedLanguage } from "@devowl-wp/api-real-cookie-banner";
import { useTranslationStatusColumns } from "./columns.js";
import { useTranslation } from "../../../../../../../others/i18n.js";
import { colors } from "../../../../../../../styles/colors.js";
import { spacing } from "../../../../../../../styles/spacing.js";
import { getSupportedLanguageOptions } from "../../../../../../../util/template/translation.js";
import { getFieldNameOrderPosition } from "../../../../../../../util/translation.js";
import { Legend } from "../translation-legend.js";
/**
 * Calculates the translation status table data.
 * @param target The target translation status.
 * @param source The source translation status.
 * @returns The translation status table data.
 */ function calculateTranslationStatusTableData(target, source) {
    const dataSource = [
        ...target?.translatableRequiredFields.map((name)=>{
            return {
                key: v4(),
                name: name,
                isOptional: false,
                baseTranslationState: getTranslationState(source.translationFlaggedFields?.MachineTranslated?.includes(name), source.translationFlaggedFields?.Fuzzy?.includes(name), source.baseValueFields?.includes(name)),
                translationState: getTranslationState(target.translationFlaggedFields?.MachineTranslated?.includes(name), target.translationFlaggedFields?.Fuzzy?.includes(name), target.translatedRequiredFields.includes(name))
            };
        }) || [],
        ...target?.translatableOptionalFields.map((name)=>{
            return {
                key: v4(),
                name: name,
                isOptional: true,
                baseTranslationState: getTranslationState(source.translationFlaggedFields?.MachineTranslated?.includes(name), source.translationFlaggedFields?.Fuzzy?.includes(name), source.baseValueFields?.includes(name)),
                translationState: getTranslationState(target.translationFlaggedFields?.MachineTranslated?.includes(name), target.translationFlaggedFields?.Fuzzy?.includes(name), target.translatedOptionalFields.includes(name))
            };
        }) || []
    ];
    dataSource.sort((a, b)=>getFieldNameOrderPosition(a.name) - getFieldNameOrderPosition(b.name));
    return dataSource;
}
/**
 * Finds the missing translation properties from the data source.
 * @param dataSource The data source containing translation status table data.
 * @returns An array of missing translation property names.
 */ function findMissingTranslationProperties(dataSource) {
    const untranslated = dataSource.map((record)=>{
        if (record.baseTranslationState !== "Missing" && // base has a translation
        record.translationState === "Missing" && // target has no translation
        !record.isOptional // is not optional
        ) {
            return record.name;
        }
        return undefined;
    }).filter(Boolean);
    return untranslated;
}
/**
 * Display translation status with a table indicating translation states of fields.
 * @param props The props of the component. See {@link ITranslationStatusProps}.
 * @returns The translation status component.
 */ function TranslationStatus(props) {
    const { targetStatus, defaultLanguageStatus, language, template, templateType, machineTranslationStatus } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();
    let isComplete = false;
    if (!isComplete && targetStatus) {
        if (Object.hasOwn(targetStatus, "isTranslationCompleteRecursive")) {
            isComplete = targetStatus.isTranslationCompleteRecursive;
        } else {
            isComplete = targetStatus.isTranslationComplete;
        }
    }
    const dataSource = useMemo(()=>calculateTranslationStatusTableData(targetStatus, defaultLanguageStatus), [
        targetStatus,
        defaultLanguageStatus,
        language
    ]);
    const [isPartialUntranslated, setPartialUntranslated] = useState(false);
    const [missingTranslations, setMissingTranslations] = useState(undefined);
    useEffect(()=>{
        const missing = findMissingTranslationProperties(dataSource);
        setMissingTranslations(missing);
        setPartialUntranslated(missing?.length > 0);
    }, [
        dataSource
    ]);
    const columns = useTranslationStatusColumns(language);
    const isLegendRowsHidden = !isComplete && !isPartialUntranslated;
    const supportedLanguages = useMemo(()=>getSupportedLanguageOptions(t), [
        t
    ]);
    const languageInfo = supportedLanguages.find((option)=>option.language === language);
    return /*#__PURE__*/ _jsxs("div", {
        style: {
            padding: spacing.layout.content.paddingMedium
        },
        children: [
            /*#__PURE__*/ _jsxs(Row, {
                style: {
                    marginBottom: spacing.layout.content.marginMedium
                },
                gutter: [
                    spacing.layout.content.marginMedium,
                    spacing.layout.content.marginSmall
                ],
                children: [
                    /*#__PURE__*/ _jsx(Col, {
                        children: /*#__PURE__*/ _jsx(Popover, {
                            content: t("Language code: {{code}}", {
                                code: languageInfo?.language
                            }),
                            children: /*#__PURE__*/ _jsx(Typography.Title, {
                                level: 3,
                                children: languageInfo?.icon
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(Col, {
                        children: /*#__PURE__*/ _jsx(Typography.Title, {
                            level: 4,
                            children: languageInfo?.label
                        })
                    }),
                    /*#__PURE__*/ _jsxs(Col, {
                        flex: "1",
                        style: {
                            textAlign: "right"
                        },
                        children: [
                            isComplete ? /*#__PURE__*/ _jsx(CheckCircleFilled, {
                                style: {
                                    color: machineTranslationStatus === EMachineTranslationStatus.Full ? colors.translation.machineTranslated : "lightgreen",
                                    marginRight: spacing.layout.content.marginSmall
                                }
                            }) : isPartialUntranslated ? /*#__PURE__*/ _jsx(WarningFilled, {
                                style: {
                                    color: "orange",
                                    marginRight: spacing.layout.content.marginSmall
                                }
                            }) : /*#__PURE__*/ _jsx(ExclamationCircleOutlined, {
                                style: {
                                    color: "red",
                                    marginRight: spacing.layout.content.marginSmall
                                }
                            }),
                            isComplete ? t("Translation complete") : isPartialUntranslated ? t("Translation incomplete") : t("Translation not yet started")
                        ]
                    })
                ]
            }),
            dataSource.length > 0 ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Row, {
                        style: {
                            textAlign: "center"
                        },
                        children: /*#__PURE__*/ _jsx(Col, {
                            children: /*#__PURE__*/ _jsx(Table, {
                                size: "small",
                                columns: columns,
                                dataSource: dataSource,
                                pagination: {
                                    hideOnSinglePage: true,
                                    defaultPageSize: 15
                                },
                                onRow: (record)=>{
                                    const style = {};
                                    if (record.baseTranslationState === "Missing") {
                                        style.opacity = "0.5";
                                    } else if (!record.isOptional && record.translationState === "Missing") {
                                        style.background = "lightgoldenrodyellow";
                                        style.fontWeight = "bolder";
                                    }
                                    return {
                                        style
                                    };
                                }
                            }, "key")
                        })
                    }),
                    /*#__PURE__*/ _jsx(Legend, {
                        style: {
                            marginBottom: spacing.layout.content.marginMedium,
                            justifyContent: "flex-end"
                        },
                        hidden: isLegendRowsHidden,
                        items: [
                            {
                                icon: /*#__PURE__*/ _jsx(ExclamationCircleOutlined, {
                                    style: {
                                        color: "red"
                                    }
                                }),
                                text: t("Missing")
                            },
                            {
                                icon: /*#__PURE__*/ _jsx(CheckCircleFilled, {
                                    style: {
                                        color: "lightgreen"
                                    }
                                }),
                                text: t("Valid")
                            },
                            {
                                icon: /*#__PURE__*/ _jsx(CheckCircleFilled, {
                                    style: {
                                        color: colors.translation.fuzzy
                                    }
                                }),
                                text: t("Fuzzy")
                            },
                            {
                                icon: /*#__PURE__*/ _jsx(CheckCircleFilled, {
                                    style: {
                                        color: colors.translation.machineTranslated
                                    }
                                }),
                                text: t("Machine translated")
                            }
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Row, {
                        style: {
                            marginTop: spacing.layout.content.marginMedium
                        },
                        children: [
                            /*#__PURE__*/ _jsx(Col, {
                                style: {
                                    flex: 1
                                }
                            }),
                            /*#__PURE__*/ _jsx(Col, {
                                children: isPartialUntranslated ? /*#__PURE__*/ _jsx(Button, {
                                    type: "primary",
                                    onClick: ()=>{
                                        const editPath = `/rcb/${templateType === "Content-Blocker" ? "content-blocker" : "service"}/edit/${template.identifier}`;
                                        const search = new URLSearchParams();
                                        search.append("translationMode", "true");
                                        search.append("sourceLanguage", ESupportedLanguage.en);
                                        search.append("targetLanguage", language);
                                        search.append("stickyTranslationMode", "true");
                                        search.append("showFormItems", missingTranslations.join(","));
                                        navigate({
                                            pathname: editPath,
                                            search: search.toString()
                                        });
                                    },
                                    children: t("Translate")
                                }) : undefined
                            })
                        ]
                    })
                ]
            }) : null
        ]
    }, `popover-status-${language}`);
}
/**
 * Returns the color of the translation state.
 * @param translationState The translation state of the field.
 * @returns The color of the translation state.
 */ function getTranslationStateColor(translationState) {
    if (translationState === "Machine translated") {
        return colors.translation.machineTranslated;
    } else if (translationState === "Fuzzy") {
        return colors.translation.fuzzy;
    } else {
        return "lightgreen";
    }
}
/**
 * Returns the translation state of a field.
 * @param isTranslationMachineTranslated Whether the translation is machine translated.
 * @param isTranslationFuzzy Whether the translation is fuzzy.
 * @param isTranslated Whether the translation is translated.
 * @returns The translation state of the field.
 */ function getTranslationState(isTranslationMachineTranslated, isTranslationFuzzy, isTranslated) {
    if (isTranslationMachineTranslated) {
        return "Machine translated";
    } else if (isTranslationFuzzy) {
        return "Fuzzy";
    } else if (isTranslated) {
        return "Valid";
    } else {
        return "Missing";
    }
}
/**
 * Returns the tooltip text for the translation state.
 * @param state The translation state of the field.
 * @param isOptional Whether the field is optional.
 * @param t The translation function.
 * @returns The tooltip text for the translation state.
 */ function getTranslationStateTooltip(state, isOptional, t) {
    switch(state){
        case "Machine translated":
            return t("Machine translated");
        case "Fuzzy":
            return t("Fuzzy");
        case "Missing":
            if (isOptional) {
                return t("Optional");
            } else {
                return t("Missing");
            }
        case "Valid":
            return t("Valid");
    }
}
export { getTranslationState, getTranslationStateColor, getTranslationStateTooltip, TranslationStatus };
